import { decodeGlued, MAGNITUDE } from "./units.js";
import { CalcError } from "./model.js";

/** A lone magnitude letter (k/m/g), case-insensitive — may be glued or spaced. */
const MAG_LETTER = /^[kmgKMG]$/;

/** A factor inside a unit-monomial, e.g. the `/d` in `img/d`. */
export interface MonoFactor {
  op: "*" | "/";
  unit: string;
  tag?: string;
}

export type Expr =
  | { t: "q"; value: number; mag: number; mono: MonoFactor[] }
  | { t: "bin"; op: "+" | "-" | "*" | "/"; l: Expr; r: Expr };

type Tok =
  | { type: "num"; value: string; space: boolean }
  | { type: "word"; value: string; space: boolean }
  | { type: "starstar"; space: boolean }
  | { type: "op"; value: "+" | "-" | "*" | "/"; space: boolean }
  | { type: "lparen"; space: boolean }
  | { type: "rparen"; space: boolean }
  | { type: "eq"; space: boolean }
  | { type: "q"; space: boolean }
  | { type: "hash"; space: boolean }
  | { type: "percent"; space: boolean }
  | { type: "comma"; space: boolean };

export function tokenize(input: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  let space = true;
  const n = input.length;
  while (i < n) {
    const c = input[i]!;
    if (c === " " || c === "\t") {
      space = true;
      i++;
      continue;
    }
    // comment to end of line
    if (c === "-" && input[i + 1] === "-") break;
    if (/[0-9.]/.test(c)) {
      let j = i + 1;
      while (j < n && /[0-9.]/.test(input[j]!)) j++;
      toks.push({ type: "num", value: input.slice(i, j), space });
      i = j;
      space = false;
      continue;
    }
    if (/[A-Za-z]/.test(c)) {
      let j = i + 1;
      while (j < n && /[A-Za-z0-9]/.test(input[j]!)) j++;
      toks.push({ type: "word", value: input.slice(i, j), space });
      i = j;
      space = false;
      continue;
    }
    if (c === "*" && input[i + 1] === "*") {
      toks.push({ type: "starstar", space });
      i += 2;
      space = false;
      continue;
    }
    if (c === "$") {
      toks.push({ type: "word", value: "$", space });
      i++;
      space = false;
      continue;
    }
    if (c === "%") {
      toks.push({ type: "percent", space });
      i++;
      space = false;
      continue;
    }
    const single: Record<string, Tok["type"]> = {
      "+": "op",
      "-": "op",
      "*": "op",
      "/": "op",
      "(": "lparen",
      ")": "rparen",
      "=": "eq",
      "?": "q",
      "#": "hash",
      ",": "comma",
    };
    const t = single[c];
    if (t === "op") toks.push({ type: "op", value: c as "+" | "-" | "*" | "/", space });
    else if (t) toks.push({ type: t, space } as Tok);
    else throw new CalcError(`unexpected character '${c}'`);
    i++;
    space = false;
  }
  return toks;
}

class TokStream {
  pos = 0;
  constructor(readonly toks: Tok[]) {}
  peek(o = 0): Tok | undefined {
    return this.toks[this.pos + o];
  }
  next(): Tok | undefined {
    return this.toks[this.pos++];
  }
  expect(type: Tok["type"]): Tok {
    const t = this.next();
    if (!t || t.type !== type) throw new CalcError(`expected ${type}`);
    return t;
  }
}

/**
 * A `#tag` comes at the **end** of a quantity, after the whole unit-expression
 * (`300 KB/user #md`), and labels the measured bucket — the numerator. So it
 * attaches to the first `*` factor (the numerator), not a `/` denominator.
 */
function parseTag(ts: TokStream, mono: MonoFactor[]): void {
  if (ts.peek()?.type === "hash") {
    ts.next();
    const w = ts.expect("word") as Extract<Tok, { type: "word" }>;
    const num = mono.find((f) => f.op === "*") ?? mono[mono.length - 1];
    if (num) num.tag = w.value;
  }
}

function parseMonoChain(ts: TokStream, mono: MonoFactor[]): void {
  // A *glued* `/unit` builds a compound unit (`req/s`, `KB/img`, `b/url`). A
  // spaced `/` (or one before a number) is arithmetic division, handled at the
  // expression level. So `x/y` is always a unit; `x / y` is always a division.
  // `*` is always quantity multiplication.
  for (;;) {
    const op = ts.peek();
    if (!op || op.type !== "op" || op.value !== "/") break;
    if (op.space) break; // spaced `/` → division, not unit composition
    const after = ts.peek(1);
    if (!after || after.type !== "word" || after.space) break;
    ts.next(); // op
    const w = ts.next() as Extract<Tok, { type: "word" }>;
    mono.push({ op: "/", unit: w.value });
  }
}

/** Parse everything after a leading number: glued magnitude/unit, `%`, unit chain. */
function parseNumericTail(ts: TokStream, value: number, leadUnit?: string): Extract<Expr, { t: "q" }> {
  let mag = 1;
  // a leading numerator unit (the `$` of `$10/crate`) must be in `mono` *before*
  // the trailing `#tag` is parsed, so the tag attaches to it, not the denominator
  const mono: MonoFactor[] = leadUnit ? [{ op: "*", unit: leadUnit }] : [];
  const glued = ts.peek();
  if (glued && glued.type === "word" && !glued.space) {
    ts.next();
    const g = decodeGlued(glued.value);
    mag = g.mag;
    if (g.unit) mono.push({ op: "*", unit: g.unit });
  } else if (glued && glued.type === "percent" && !glued.space) {
    ts.next();
    mag *= 0.01; // dimensionless
  }
  // a *spaced* magnitude letter is also legal (`2 g`, `5 m`) — magnitudes aren't
  // units, so there's no slot ambiguity now that b/B always mean byte.
  if (mag === 1 && mono.length === 0) {
    const w = ts.peek();
    if (w && w.type === "word" && MAG_LETTER.test(w.value)) {
      ts.next();
      mag = MAGNITUDE[w.value.toLowerCase()] ?? 1;
    }
  }
  if (mono.length === 0) {
    const w = ts.peek();
    if (w && w.type === "word" && w.value !== "of" && !MAG_LETTER.test(w.value)) {
      ts.next();
      mono.push({ op: "*", unit: w.value });
    }
  }
  parseMonoChain(ts, mono);
  parseTag(ts, mono); // a trailing `#tag` (after the whole unit-expr)
  return { t: "q", value, mag, mono };
}

function parseFactor(ts: TokStream): Expr {
  const t = ts.peek();
  if (!t) throw new CalcError("unexpected end of expression");

  if (t.type === "lparen") {
    ts.next();
    const e = parseExpr(ts);
    ts.expect("rparen");
    return e;
  }

  // money prefix: `$5`, `$5M/mo`, `$1.20/req`
  if (t.type === "word" && t.value === "$") {
    const nx = ts.peek(1);
    if (nx && nx.type === "num") {
      ts.next(); // $
      const numTok = ts.next() as Extract<Tok, { type: "num" }>;
      return parseNumericTail(ts, parseFloat(numTok.value), "$");
    }
    ts.next(); // bare `$` unit, value 1
    const mono: MonoFactor[] = [{ op: "*", unit: "$" }];
    parseMonoChain(ts, mono);
    parseTag(ts, mono);
    return { t: "q", value: 1, mag: 1, mono };
  }

  if (t.type === "num") {
    ts.next();
    return parseNumericTail(ts, parseFloat(t.value));
  }

  if (t.type === "word") {
    ts.next();
    const mono: MonoFactor[] = [{ op: "*", unit: t.value }];
    parseMonoChain(ts, mono);
    parseTag(ts, mono);
    return { t: "q", value: 1, mag: 1, mono };
  }

  throw new CalcError(`unexpected token '${t.type}'`);
}

function parseTerm(ts: TokStream): Expr {
  let left = parseFactor(ts);
  for (;;) {
    const op = ts.peek();
    if (op && op.type === "op" && (op.value === "*" || op.value === "/")) {
      ts.next();
      const right = parseFactor(ts);
      left = { t: "bin", op: op.value, l: left, r: right };
    } else if (op && op.type === "word" && op.value === "of") {
      // `5% of 10M` reads as 5% * 10M
      ts.next();
      const right = parseFactor(ts);
      left = { t: "bin", op: "*", l: left, r: right };
    } else break;
  }
  return left;
}

export function parseExpr(ts: TokStream): Expr {
  let left = parseTerm(ts);
  for (;;) {
    const op = ts.peek();
    if (op && op.type === "op" && (op.value === "+" || op.value === "-")) {
      ts.next();
      const right = parseTerm(ts);
      left = { t: "bin", op: op.value, l: left, r: right };
    } else break;
  }
  return left;
}

/** Parse a complete expression string (no trailing tokens allowed). */
export function parseExpression(src: string): Expr {
  const ts = new TokStream(tokenize(src));
  const e = parseExpr(ts);
  if (ts.peek()) throw new CalcError("trailing tokens after expression");
  return e;
}

// ---- Query segment (right of `=`) -----------------------------------------

export interface SpecFactor {
  op: "*" | "/";
  unit: string; // "?" = wildcard
  tag?: string;
  /** optional coefficient on the unit, e.g. the `5` in `?/5y` (per 5 years). */
  coeff?: number;
}

export type QuerySpec =
  | { kind: "auto" }
  | { kind: "all" }
  | { kind: "unit"; factors: SpecFactor[] };

export function parseSegment(src: string): QuerySpec {
  const trimmed = src.trim();
  if (trimmed === "") return { kind: "auto" };
  const ts = new TokStream(tokenize(trimmed));

  // optional leading '?'
  if (ts.peek()?.type === "q") ts.next();
  if (!ts.peek()) return { kind: "auto" };
  if (ts.peek()?.type === "starstar") return { kind: "all" };

  const factors: SpecFactor[] = [];
  let first = true;
  for (;;) {
    const p = ts.peek();
    if (!p) break;

    // standalone tag (e.g. `#md`) → wildcard numerator filtered by tag
    if (p.type === "hash") {
      ts.next();
      const w = ts.expect("word") as Extract<Tok, { type: "word" }>;
      factors.push({ op: "*", unit: "?", tag: w.value });
      first = false;
      continue;
    }

    let op: "*" | "/" = "*";
    if (p.type === "op" && (p.value === "*" || p.value === "/")) {
      op = p.value;
      ts.next();
    } else if (!first) {
      break; // need an operator between factors
    }

    // optional coefficient on a denominator, e.g. `/5y` → per 5 years. Only after
    // `/` — a leading number is a stale result (`= 20k req/mo`), handled as auto.
    let coeff: number | undefined;
    if (op === "/" && ts.peek()?.type === "num") {
      coeff = parseFloat((ts.next() as Extract<Tok, { type: "num" }>).value);
    }

    const u = ts.peek();
    let unit: string;
    if (u?.type === "q") {
      ts.next();
      unit = "?";
    } else if (u?.type === "percent") {
      ts.next();
      unit = "%";
    } else if (u?.type === "word") {
      ts.next();
      unit = u.value;
    } else if (coeff !== undefined) {
      break; // a bare number with no unit isn't a valid spec factor
    } else {
      break;
    }
    const f: SpecFactor = coeff !== undefined ? { op, unit, coeff } : { op, unit };
    if (ts.peek()?.type === "hash") {
      ts.next();
      const w = ts.expect("word") as Extract<Tok, { type: "word" }>;
      f.tag = w.value;
    }
    factors.push(f);
    first = false;
  }

  if (factors.length === 0) return { kind: "auto" };
  return { kind: "unit", factors };
}
